import React, { useEffect, useMemo, useState } from 'react';
import { EditIcon } from 'lucide-react';
import { Toast } from '../Toast';
import { useClientDetails } from '../../hooks/useClientDetails';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface InfoTabProps {
  clientId: string;
  onNavigateToTab?: (tab: string) => void;
}

type FormState = {
  name: string;
  phone: string;
  email: string;
  age: string;
  gender: string;
  goal: string;
  medications: string;
  allergies: string;
  dietType: string;
  workHours: string;
  sleepPattern: string;
  notes: string;
};

const emptyFormState: FormState = {
  name: '',
  phone: '',
  email: '',
  age: '',
  gender: '',
  goal: '',
  medications: '',
  allergies: '',
  dietType: '',
  workHours: '',
  sleepPattern: '',
  notes: '',
};

function normalizeNullable(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function InfoTab({ clientId, onNavigateToTab }: InfoTabProps) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<FormState>(emptyFormState);
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientQuery = useClientDetails(clientId);
  const client = clientQuery.data;

  useEffect(() => {
    if (!client) return;
    setFormData({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
      age: client.age?.toString() || '',
      gender: client.gender || '',
      goal: client.goal || '',
      medications: client.medical_medications || '',
      allergies: (client.medical_allergies ?? []).join(', '),
      dietType: client.diet_type || '',
      workHours: client.work_hours || '',
      sleepPattern: client.sleep_pattern || '',
      notes: client.general_notes || '',
    });
  }, [client]);

  const mutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      return apiRequest(`/api/clients/${clientId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }, session?.access_token);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['client', clientId], data);
      queryClient.invalidateQueries({ queryKey: ['timeline', clientId] });
      setShowToast(true);
    },
  });

  const handleSave = async () => {
    if (!client) return;
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Phone is required');
      return;
    }

    // Validate email if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate age if provided
    if (formData.age) {
      const ageNum = parseInt(formData.age);
      if (isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
        setError('Age must be between 1 and 120');
        return;
      }
    }

    setError(null);

    const allergiesArray = formData.allergies
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: normalizeNullable(formData.email),
      age: formData.age ? parseInt(formData.age) : null,
      gender: normalizeNullable(formData.gender),
      goal: normalizeNullable(formData.goal),
      medical_medications: normalizeNullable(formData.medications),
      medical_allergies: allergiesArray,
      diet_type: normalizeNullable(formData.dietType),
      work_hours: normalizeNullable(formData.workHours),
      sleep_pattern: normalizeNullable(formData.sleepPattern),
      general_notes: normalizeNullable(formData.notes),
    };

    try {
      await mutation.mutateAsync(payload);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    }
  };

  if (clientQuery.isError) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-red-200 text-center">
          <p className="text-sm text-red-500">
            Unable to load client information. Please refresh.
          </p>
        </div>
      </div>
    );
  }

  if (clientQuery.isLoading || !client) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 text-center">
          <p className="text-sm text-gray-500">Loading client information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-[#212121]">Client Information</h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="text-[#5B8E87] font-medium text-sm flex items-center gap-1"
          >
            <EditIcon className="w-4 h-4" />
            Edit All
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={mutation.isPending}
            className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-1 ${
              mutation.isPending
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-[#5B8E87] text-white'
            }`}
          >
            {mutation.isPending ? 'Saving...' : 'Save All'}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-xl">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
        <h3 className="font-semibold text-base text-[#212121] mb-4">Profile</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={!isEditing}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              disabled={!isEditing}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!isEditing}
              placeholder="client@example.com"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Age
              </label>
              <input
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                disabled={!isEditing}
                placeholder="25"
                min="1"
                max="120"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                disabled={!isEditing}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-binary">Non-binary</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Goal
            </label>
            <input
              type="text"
              value={formData.goal}
              onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
              disabled={!isEditing}
              placeholder="e.g., Lose 10kg, Manage PCOD, Build muscle"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
        <h3 className="font-semibold text-base text-[#212121] mb-4">Medical</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Medications
            </label>
            <input
              type="text"
              value={formData.medications}
              onChange={(e) =>
                setFormData({ ...formData, medications: e.target.value })
              }
              disabled={!isEditing}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Allergies (comma separated)
            </label>
            <input
              type="text"
              value={formData.allergies}
              onChange={(e) =>
                setFormData({ ...formData, allergies: e.target.value })
              }
              disabled={!isEditing}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
        <h3 className="font-semibold text-base text-[#212121] mb-4">Lifestyle</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Diet Type
            </label>
            <select
              value={formData.dietType}
              onChange={(e) =>
                setFormData({ ...formData, dietType: e.target.value })
              }
              disabled={!isEditing}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            >
              <option value="">Select</option>
              <option value="Vegetarian">Vegetarian</option>
              <option value="Non-Vegetarian">Non-Vegetarian</option>
              <option value="Vegan">Vegan</option>
              <option value="Eggetarian">Eggetarian</option>
              <option value="Jain">Jain</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Work Hours
            </label>
            <input
              type="text"
              value={formData.workHours}
              onChange={(e) =>
                setFormData({ ...formData, workHours: e.target.value })
              }
              disabled={!isEditing}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Sleep Pattern
            </label>
            <input
              type="text"
              value={formData.sleepPattern}
              onChange={(e) =>
                setFormData({ ...formData, sleepPattern: e.target.value })
              }
              disabled={!isEditing}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
        <h3 className="font-semibold text-base text-[#212121] mb-4">
          General Notes
        </h3>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          disabled={!isEditing}
          rows={4}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none disabled:bg-gray-50"
        />
      </div>

      {showToast && (
        <Toast message="Profile updated" onClose={() => setShowToast(false)} />
      )}
    </div>
  );
}
